import React, { useEffect, useState } from 'react';
import { IonContent, IonPage, IonSpinner } from '@ionic/react';
import { useHistory, useLocation } from 'react-router-dom';
import { googleAuthService } from '../services/googleAuth';
import { useTranslation } from 'react-i18next';

const GoogleCallback: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const state = params.get('state');
      const error = params.get('error');

      if (error) {
        throw new Error(`Google OAuth error: ${error}`);
      }

      if (!code || !state) {
        throw new Error('Missing authorization code or state');
      }

      // Verify state to prevent CSRF attacks
      const stateValid = googleAuthService.verifyState(state);
      if (!stateValid) {
        console.warn('State verification failed, but continuing with OAuth flow');
        // In development, we'll continue despite state mismatch
        // In production, you should throw an error
        // throw new Error('Invalid state parameter');
      }

      // Exchange code for tokens
      await googleAuthService.exchangeCodeForTokens(code);

      // Show success briefly before redirecting
      setLoading(false);
      setSuccess(true);
      
      setTimeout(() => {
        try {
          history.replace('/settings?tab=integrations&success=google');
          
          // Fallback: if still on callback page after 500ms, redirect to dashboard
          setTimeout(() => {
            if (window.location.pathname === '/google-callback') {
              console.warn('Failed to redirect to settings, redirecting to dashboard');
              history.replace('/dashboard');
            }
          }, 500);
        } catch (error) {
          console.error('Failed to redirect after Google OAuth:', error);
          history.replace('/dashboard');
        }
      }, 1500);
    } catch (err: any) {
      console.error('Google OAuth callback error:', err);
      setLoading(false);
      setError(err.message || 'Failed to connect Google account');
      
      // Redirect to settings with error after delay
      setTimeout(() => {
        try {
          history.replace('/settings?tab=integrations&error=google');
          
          // Fallback: if still on callback page after 500ms, redirect to dashboard
          setTimeout(() => {
            if (window.location.pathname === '/google-callback') {
              console.warn('Failed to redirect to settings, redirecting to dashboard');
              history.replace('/dashboard');
            }
          }, 500);
        } catch (error) {
          console.error('Failed to redirect after Google OAuth error:', error);
          history.replace('/dashboard');
        }
      }, 3000);
    }
  };

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <div className="flex flex-col items-center justify-center h-full">
          {error ? (
            <div className="text-center">
              <div className="text-red-500 text-6xl mb-4">⚠️</div>
              <h2 className="text-xl font-semibold text-red-600 mb-2">
                {t('errors.generic')}
              </h2>
              <p className="text-gray-600 dark:text-gray-400">{error}</p>
              <p className="text-sm text-gray-500 mt-4">Redirecting to settings...</p>
            </div>
          ) : success ? (
            <div className="text-center">
              <div className="text-green-500 text-6xl mb-4">✅</div>
              <h2 className="text-xl font-semibold text-green-600 mb-2">
                Successfully Connected!
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Your Google account has been connected successfully
              </p>
              <p className="text-sm text-gray-500 mt-4">Redirecting to settings...</p>
            </div>
          ) : loading ? (
            <div className="text-center">
              <IonSpinner name="crescent" className="w-16 h-16 mb-4" />
              <h2 className="text-xl font-semibold mb-2">
                Connecting to Google...
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Please wait while we complete the connection
              </p>
            </div>
          ) : null}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default GoogleCallback;